import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Award, Clock, ArrowRight, MessageCircle } from 'lucide-react';
import { serviceImages, heroImages, companyInfo } from '../data/mock';

const NVQCoursesPage = () => {
  const nvqLevels = [
    {
      level: "Level 2",
      title: "NVQ Diploma Level 2",
      suitable: "Skilled Workers",
      duration: "6-8 weeks",
      price: "From £787",
      card: "Blue CSCS Card",
      trades: [
        "Bricklaying",
        "Carpentry & Joinery",
        "Painting & Decorating",
        "Plastering",
        "Groundworking",
        "Plant Operations",
        "Electrical Installation",
        "Plumbing"
      ],
      benefits: [
        "Industry-recognized qualification",
        "Valid for 5 years",
        "All evidence remotely assessed",
        "Portfolio building support",
        "Fast track options available"
      ]
    },
    {
      level: "Level 3",
      title: "NVQ Diploma Level 3",
      suitable: "Advanced/Supervisory",
      duration: "8-12 weeks",
      price: "From £1,120",
      card: "Gold CSCS Card",
      trades: [
        "Site Supervision",
        "Advanced Carpentry",
        "Advanced Bricklaying",
        "Site Management",
        "Specialist Installations"
      ],
      benefits: [
        "Supervisory level qualification",
        "Career progression pathway",
        "Higher earning potential",
        "Management responsibilities",
        "Expert assessor support"
      ],
      popular: true
    },
    {
      level: "Level 6",
      title: "NVQ Diploma Level 6",
      suitable: "Construction Managers",
      duration: "12-16 weeks",
      price: "From £1,480",
      card: "Black CSCS Card",
      trades: [
        "Construction Site Management",
        "Project Management",
        "Contract Management",
        "Senior Site Supervision"
      ],
      benefits: [
        "Highest level qualification",
        "Management & leadership",
        "Strategic planning skills",
        "Senior positions",
        "Comprehensive portfolio support"
      ]
    },
    {
      level: "Level 7",
      title: "NVQ Diploma Level 7",
      suitable: "Senior Managers",
      duration: "16-20 weeks",
      price: "From £1,651",
      card: "Black CSCS Card",
      trades: [
        "Strategic Construction Management",
        "Senior Project Management",
        "Construction Director",
        "Operations Management"
      ],
      benefits: [
        "Executive level qualification",
        "Leadership & strategy",
        "Board-level positions",
        "Industry authority",
        "Dedicated senior assessor"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={serviceImages.training} 
            alt="NVQ Training" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              NVQ Courses Level 2-7
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Professional construction qualifications to advance your career and secure your CSCS card
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              Enroll Today
            </a>
          </div>
        </div>
      </section>

      {/* What is NVQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-6">
              What is an NVQ?
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                NVQ (National Vocational Qualification) is a work-based qualification that recognizes the skills and knowledge you have gained through practical experience in your trade. Unlike classroom-based qualifications, NVQs are assessed based on your actual work performance.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>NVQs are essential for obtaining skilled worker CSCS cards (Blue, Gold, and Black cards)</strong> and are highly valued by employers across the construction industry.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                    Why Choose NVQ?
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={18} />
                      <span>Work-based assessment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={18} />
                      <span>No exams or classrooms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={18} />
                      <span>Industry-recognized</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={18} />
                      <span>Career progression</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-construction-darkblue/5 border-l-4 border-construction-darkblue p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                    Our NVQ Process
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-darkblue flex-shrink-0 mt-1" size={18} />
                      <span>Remote portfolio building</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-darkblue flex-shrink-0 mt-1" size={18} />
                      <span>Dedicated assessor support</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-darkblue flex-shrink-0 mt-1" size={18} />
                      <span>Flexible site visits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-darkblue flex-shrink-0 mt-1" size={18} />
                      <span>Fast track options</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NVQ Levels */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
              NVQ Qualification Levels
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the NVQ level that matches your experience and career goals
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {nvqLevels.map((nvq, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-8 border-2 ${
                  nvq.popular 
                    ? 'border-construction-yellow shadow-xl' 
                    : 'border-gray-200 shadow-sm'
                } hover:shadow-xl transition-all relative`}
              >
                {nvq.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-construction-yellow text-construction-darkblue px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block bg-construction-yellow/20 text-construction-darkblue px-3 py-1 rounded-full text-sm font-bold mb-2">
                      {nvq.level}
                    </span>
                    <h3 className="text-2xl font-bold text-construction-darkblue mb-1">
                      {nvq.title}
                    </h3>
                    <p className="text-construction-yellow font-semibold">
                      {nvq.suitable}
                    </p>
                  </div>
                  <Award className="text-construction-yellow" size={40} />
                </div>

                <div className="flex items-center gap-6 mb-6 pb-6 border-b border-gray-200">
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-bold text-construction-darkblue flex items-center gap-1">
                      <Clock size={16} />
                      {nvq.duration}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Price</p>
                    <p className="font-bold text-construction-darkblue text-xl">
                      {nvq.price}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Leads to</p>
                    <p className="font-bold text-construction-yellow">
                      {nvq.card}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-construction-darkblue mb-3">
                    Available Trades:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {nvq.trades.map((trade, idx) => (
                      <span
                        key={idx}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {trade}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-construction-darkblue mb-3">
                    Benefits:
                  </h4>
                  <ul className="space-y-2">
                    {nvq.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={`tel:${companyInfo.phone}`}
                  className={`block text-center px-6 py-3 rounded-lg font-bold transition-all hover:scale-105 ${
                    nvq.popular
                      ? 'bg-construction-yellow text-construction-darkblue hover:bg-yellow-500'
                      : 'bg-construction-darkblue text-white hover:bg-black'
                  }`}
                >
                  Enroll Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How NVQ Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-12 text-center">
              How Our NVQ Process Works
            </h2>
            
            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title: "Initial Consultation",
                  description: "We discuss your trade, experience, and career goals to determine the right NVQ level for you."
                },
                {
                  step: 2,
                  title: "Portfolio Building",
                  description: "Your dedicated assessor helps you build your evidence portfolio remotely using photos, videos, and documentation from your work."
                },
                {
                  step: 3,
                  title: "Site Assessment",
                  description: "We arrange convenient site visits to observe your practical skills and verify your competence in real working conditions."
                },
                {
                  step: 4,
                  title: "Qualification & Card",
                  description: "Once completed, you receive your NVQ certificate and can apply for your CSCS card immediately."
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-6 bg-gray-50 p-6 rounded-xl">
                  <div className="bg-construction-yellow text-construction-darkblue w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-construction-darkblue mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-construction-darkblue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Start Your NVQ Journey Today
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Advance your construction career with a recognized NVQ qualification
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              {companyInfo.phone}
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in NVQ courses`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105"
            >
              Enquire Now
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NVQCoursesPage;
